import {
  Component,
  Inject,
  ChangeDetectionStrategy,
  OnInit,
  signal,
  computed,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatListModule } from '@angular/material/list';
import {
  MenuDialogData,
  MenuDialogToggle,
  MenuDialogAction,
  PeoplePickerList,
} from '../../model';
import { PeoplePickerComponent } from '../people-picker/people-picker/people-picker.component';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';

@Component({
  selector: 'app-menu-dialog',
  imports: [
    MatDialogModule,
    MatSlideToggleModule,
    MatListModule,
    PeoplePickerComponent,
    MatIconModule,
    TranslateModule,
    FormsModule,
    MatIconButton,
    MatButtonModule,
    MatCheckboxModule,
  ],
  templateUrl: './menu-dialog.component.html',
  styleUrl: './menu-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuDialogComponent implements OnInit {
  public selectedItems: (string | number)[] = [];
  public toggles: Record<string, boolean> = {};
  public selectedPeoplesData: string[] = [];
  public selectedItemIndex: number[] = [];
  public includeBakers = signal<boolean>(false);

  constructor(
    public _dialogRef: MatDialogRef<MenuDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MenuDialogData,
  ) {
    if (data.toggles) {
      data.toggles.forEach((toggle: MenuDialogToggle) => {
        this.toggles[toggle.label] = toggle.default || false;
      });
    }
  }

  ngOnInit() {
    // Initialize any necessary data or state here
    this.selectedItems = [];
  }

  /**
   * Toggles the selection of an item in the list.
   *
   * @param {string} label
   * @memberof MenuDialogComponent
   */
  public toggleItem(label: string) {
    const index = this.selectedItems.indexOf(label);
    if (index === -1) {
      this.selectedItems.push(label);
    } else {
      this.selectedItems.splice(index, 1);
    }
  }

  /**
   * Deselects one item from `selectedItems` based on the condition
   * @memberof MenuDialogComponent
   */
  public deselectItemChange() {
    if (this.selectedItems.length > 4 && this.includeBakers()) {
      for (let i = (this.data.list?.length as number) - 1; i >= 0; i--) {
        if (this.data.list?.[i]) {
          const id = this.data.list?.[i].recipeVariantId;
          if (this.selectedItems.includes(id)) {
            this.selectedItems = this.selectedItems.filter((v) => v !== id);
            break;
          }
        }
      }
    }
  }

  /**
   * Executes the action as per the functionality given for the dialog buttons.
   *
   * @param {(((_action: MenuDialogAction) => void) | undefined)} [fn]
   * @memberof MenuDialogComponent
   */
  public action(
    fn?:
      | ((_action: MenuDialogAction) => void | { shouldClose?: boolean })
      | undefined,
  ) {
    if (fn) {
      const allSelectedItems = [...this.selectedItems];
      const result: MenuDialogAction = {
        selectedItems: allSelectedItems,
        selectedItemIndex: this.selectedItemIndex,
        includeBakers: this.includeBakers(),
        input:
          this.selectedPeoplesData.length > 0
            ? {
                label: 'selectedPeoples',
                value: this.selectedPeoplesData.join(', '),
              }
            : undefined,
        toggles: this.toggles,
      };
      const res = fn(result);
      if (res && res.shouldClose) {
        this._dialogRef.close();
      }
    }
  }
  /**
   * Closes the dialog without any action.
   *
   * @memberof MenuDialogComponent
   */
  public closeDialog() {
    this._dialogRef.close();
  }
  /**
   * Handles the selection of people from the people picker component.
   *
   * @param {PeoplePickerList[]} event
   * @memberof MenuDialogComponent
   */
  public selectedPeoples(event: PeoplePickerList[]) {
    this.selectedPeoplesData = event.map((item) => item.mail);
  }
  /**
   * Checks if the item at the given index is disabled and prevents default action.
   *
   * @param {number} index
   * @param {MouseEvent} event
   * @memberof MenuDialogComponent
   */
  public isDisabled(index: number, event: MouseEvent) {
    if (index === 0) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  /**
   * Dynamic subtitle based on includeBakers checkbox state
   */
  public dynamicSubtitle = computed(() => {
    if (this.data.isBakersEnabled && this.includeBakers()) {
      return 'selectThreeFormulasToInclude';
    }
    return this.data.subtitle;
  });
}
